import omit from 'lodash/omit';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { collectChartCandidates } from './chart-inventory-source';
import { collectTableCandidates } from './table-inventory-source';
import { INVENTORY_EXCLUDE_ATTRIBUTE } from './screen-inventory.constants';
import type { InventoryCandidate, InventorySource, PageDataResult, ScreenInventoryItem } from './screen-inventory.types';

const sources: InventorySource[] = [collectChartCandidates, collectTableCandidates];

function hashText (text: string): string {
    let hash = 0;
    for (const character of text) {
        hash = (hash * 31 + (character.codePointAt(0) ?? 0)) % 4294967296;
    }

    return hash.toString(36);
}

// Text with no Latin letters or digits, such as a Japanese title, slugs to a hash so its id stays stable.
function toSlug (text: string): string {
    const slug = text
        .normalize('NFKD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40)
        .replace(/-$/, '');

    return slug || (text.trim() ? hashText(text.trim()) : '');
}

/** Candidates that read the same take a #n suffix by position, so the same screen always gets the same ids. */
export function createInventoryIds (routeName: string, candidates: Array<Pick<InventoryCandidate, 'kind' | 'title' | 'fallbackName'>>): string[] {
    const seen: Record<string, number> = {};

    return candidates.map((candidate) => {
        const slug = toSlug(candidate.title || candidate.fallbackName || '');
        const base = [routeName, candidate.kind, slug].filter(Boolean).join(':');
        seen[base] = (seen[base] ?? 0) + 1;

        return seen[base] === 1 ? base : `${base}#${seen[base]}`;
    });
}

function compareDocumentOrder (first: InventoryCandidate, second: InventoryCandidate): number {
    if (first.element === second.element) return 0;

    // eslint-disable-next-line no-bitwise -- compareDocumentPosition returns a bitmask
    return first.element.compareDocumentPosition(second.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

// jsdom has no checkVisibility, and TypeScript 4.9 does not type it.
function isVisible (element: Element & { checkVisibility?: (options?: Record<string, boolean>) => boolean }): boolean {
    return typeof element.checkVisibility === 'function'
        ? element.checkVisibility({ visibilityProperty: true, opacityProperty: true })
        : element.isConnected;
}

function collectFromSource (source: InventorySource): InventoryCandidate[] {
    try {
        return source();
    } catch (error) {
        console.error('Leo screen inventory source failed', error);

        return [];
    }
}

type ResolvedCandidate = InventoryCandidate & { id: string };

function resolveScreenCandidates (routeName: string): ResolvedCandidate[] | null {
    if (!hasSegmentFlag('leoScreenInventory')) return null;

    const candidates = sources
        .flatMap(collectFromSource)
        .filter(({ element }) => element.isConnected && !element.closest(`[${INVENTORY_EXCLUDE_ATTRIBUTE}]`))
        .sort(compareDocumentOrder);
    const ids = createInventoryIds(routeName, candidates);

    return candidates.map((candidate, index) => ({ ...candidate, id: ids[index] }));
}

function toItem (candidate: ResolvedCandidate): ScreenInventoryItem {
    return {
        ...omit(candidate, ['element', 'fallbackName', 'read']),
        visible: isVisible(candidate.element),
    };
}

/** Returns null when the flag is off, which callers must tell apart from a screen with nothing on it. */
export function collectScreenInventory (routeName: string): ScreenInventoryItem[] | null {
    return resolveScreenCandidates(routeName)?.map(toItem) ?? null;
}

function resolveScreenItem (routeName: string, itemId: string): { item?: ResolvedCandidate; onScreenNow: string[] } {
    const candidates = resolveScreenCandidates(routeName) ?? [];

    return {
        item: candidates.find((candidate) => candidate.id === itemId),
        onScreenNow: candidates.map((candidate) => candidate.id),
    };
}

export function readScreenItem (routeName: string, itemId: string): PageDataResult {
    const { item, onScreenNow } = resolveScreenItem(routeName, itemId);

    if (!item) {
        return {
            error: {
                code: 'not_on_screen',
                message: `Nothing with id "${itemId}" is on the user's screen now.`,
                onScreenNow,
            },
        };
    }

    if (!item.read) return toItem(item);

    const data = item.read();
    if ('error' in data) return data;

    return { id: item.id, kind: item.kind, ...(item.title && { title: item.title }), ...data };
}
